import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { setOrderAction } from '../../app/actions/SetOrderAction';
import { contentCountsUpToDate } from '../../app/actions/spaces/ContentCountsActions';
import { enterSpace } from '../../app/actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../app/actions/spaces/FavoritesActions';
import { requestSpaceContents } from '../../app/actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../app/actions/spaces/SetPreviousUrlReducerAction';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs } from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { ServiceLocator } from '../../app/ServiceLocator';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../../app/spaces/RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../../app/spaces/RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../../app/spaces/RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { DataRoleSubspaceName, SpaceInfo } from '../../app/state/SpaceInfo';
import { MapDispatchToProps } from '../../app/utils/ConnectUtils';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { ReactDirectiveProps } from '../../ts/directives/reactDirectiveFactory';
import { Filter } from '../../ts/helpers/Filter';
import { Omit } from '../../ts/helpers/TypescriptHelpers';
import { DataRoleDefinition } from '../../ts/models/ContentDefinitions';
import { IDataRoleDetailInfo, IDataRoleInfo } from '../../ts/models/IDataRoleInfo';
import { DataRoles } from '../../ts/resources/Dataroles';
import { Favorites } from '../../ts/resources/Favorites';
import { Projects } from '../../ts/resources/Projects';
import { IDataRoleActions } from '../../ts/ServerApi/types/IDataRoleActions';
import { FavoriteItemType } from '../../ts/ServerApi/types/IFavorite';
import { BrowserTitleService } from '../../ts/services/BrowserTitleService';
import { Telemetry } from '../../ts/services/TelemetryService';
import { ConnectedDataRoleSpace } from './ConnectedDataRoleSpace';
import { DataRoleSpaceOwnProps, DataRoleSpaceProps } from './DataRoleSpace';

export interface DataRoleSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly routeLocationSearch: string;
  readonly subspaceName: DataRoleSubspaceName;
  readonly dataRoleId: string;
  readonly urls: SpacesSiteUrls;
  readonly renderDataRoleSpace: (props: DataRoleSpaceOwnProps) => JSX.Element;
  readonly fetchedAllData: () => void;
}

export interface DataRoleSpaceProviderDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly addContentFavorite: (contentId: string, contentType: FavoriteItemType) => void;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: FavoriteItemType, contentName: string) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export interface DataRoleSpaceProviderPropsFromAngular {
  readonly dataRoles: DataRoles;
  readonly telemetry: Telemetry;
  readonly browserTitleService: BrowserTitleService;
  readonly projects: Projects;
  readonly favorites: Favorites;
}

export type DataRoleSpaceNonAngularProps =  DataRoleSpaceProviderOwnProps & DataRoleSpaceProviderDispatchProps;

export type DataRoleSpaceProviderContainerProps = DataRoleSpaceProviderOwnProps & DataRoleSpaceProviderDispatchProps & DataRoleSpaceProviderPropsFromAngular;

export interface DataRoleSpaceProviderContainerState {
  readonly dataRoleActions: IDataRoleActions;
  readonly dataRoleInfo: IDataRoleInfo;
  readonly dataRoleDetailInfo: IDataRoleDetailInfo;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
}

const InitialState: DataRoleSpaceProviderContainerState = {
  dataRoleActions: undefined,
  dataRoleDetailInfo: undefined,
  dataRoleInfo: undefined,
  initialRender: true,
  renderChildren: false,
  hasError: false
};

export class DataRoleSpaceProviderContainer extends React.PureComponent<DataRoleSpaceProviderContainerProps, DataRoleSpaceProviderContainerState> {

  private unmounted: boolean = false;

  public constructor(props: DataRoleSpaceProviderContainerProps){
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderDataRoleSpace({
        allowedActions: this.state.dataRoleActions,
        dataRoleDetailInfoWithUsage: {
          usageInfo: this.state.dataRoleInfo.usageInfo,
          usageInfoMaxValues: this.state.dataRoleInfo.usageInfoMaxValues,
          ...this.state.dataRoleDetailInfo,
        },
        subspaceProps: {
          siteUrls: this.props.urls,
          dataRoleDetailInfo: this.state.dataRoleDetailInfo,
          angularContext: this.props.angularContext,
        },
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite,
        telemetry: this.props.telemetry,
        angularContext: this.props.angularContext,
      });
    } else if (this.state.hasError) {
      return <RouterError errorType={'dataroles'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: DataRoleSpaceProviderContainerProps) {
    if (this.changedDataRole(prevProps)) {
      this.updateState(InitialState /* want the initial state for the new data role */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace(false);

      this.setState({initialRender: false, renderChildren: true});
      this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.dataRoleDetailInfo.name });

    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      this.changeSubspace(true);
    }
  }

  private getData = () => {
    this.getDataRole();
    this.getDataRoleDetail();
    this.getActions();
  }

  private changedDataRole(prevProps: DataRoleSpaceProviderContainerProps) {
    return !_.isUndefined(prevProps.dataRoleId)
      && !_.isUndefined(this.props.dataRoleId)
      && prevProps.dataRoleId !== this.props.dataRoleId;
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.dataRoleInfo) &&
      !_.isUndefined(this.state.dataRoleDetailInfo) &&
      !_.isUndefined(this.state.dataRoleActions);
  }

  private getDataRoleDetail = async () => {
    try {
      const dataRoleDetail = await this.props.dataRoles.getById(this.props.dataRoleId);
      this.updateState({dataRoleDetailInfo: dataRoleDetail});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getDataRole = async () => {
    try {
      const dataRole = await this.props.dataRoles.all()
        .withFilters([Filter.fieldEq('id', this.props.dataRoleId)])
        .getFirst();
      this.updateState({dataRoleInfo: dataRole});
      const projectHierarchy = await this.props.projects.getProjectHierarchy(dataRole.project.id);
      const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
      populateContentBreadcrumbs([ ...projectHierarchyWithType, dataRole ], this.props.urls);
      this.props.fetchedAllData();
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getActions = async () => {
    try {
      const actions = await this.props.dataRoles.actions(this.props.dataRoleId);
      this.updateState({dataRoleActions: actions});
    } catch (e) {}
  }

  private updateState<K extends keyof DataRoleSpaceProviderContainerState>(newState: Pick<DataRoleSpaceProviderContainerState, K>): void {
    if (!this.unmounted) {
      this.setState(newState);
    }
  }

  private changeSubspace = (shouldNotFetchCounts: boolean) => {
    this.props.enterSpace(
      {
        item: this.state.dataRoleDetailInfo,
        spaceType: DataRoleDefinition.contentType,
        subspaceName: this.props.subspaceName
      }
    );

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if(shouldNotFetchCounts) {
      this.props.contentCountsUpToDate();
    }

    this.props.requestSpaceContents();
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.dataRoleDetailInfo.id, 'dataRole');
  }

  private unfavorite = () => {
   this.props.removeContentFavorite(this.state.dataRoleDetailInfo.id, this.state.dataRoleDetailInfo.id, 'dataRole', this.state.dataRoleDetailInfo.name);
  }
}

export namespace DataRoleSpaceProviderAngularProvider {

  function getAngularServicesWithServiceLocator() {
    const dataRoles = ServiceLocator.get<DataRoles>('DataRoles');
    const telemetry = ServiceLocator.get<Telemetry>('TelemetryService');
    const browserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');
    const projects = ServiceLocator.get<Projects>('Projects');
    const favorites = ServiceLocator.get<Favorites>('Favorites');
    return {
      dataRoles,
      telemetry,
      browserTitleService,
      projects,
      favorites
    };
  }

  function selectPropsFromAngularServices(angularServices: DataRoleSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<DataRoleSpaceNonAngularProps, DataRoleSpaceProviderPropsFromAngular, DataRoleSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const DataRoleSpaceProviderAng: React.ComponentType<DataRoleSpaceNonAngularProps> = DataRoleSpaceProviderAngularProvider.HOC(DataRoleSpaceProviderContainer);

const mapDispatchToProps: MapDispatchToProps<DataRoleSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  addContentFavorite,
  removeContentFavorite,
  setOrderAction,
  setPreviousUrl,
}, dispatch);

export const ConnectedDataRoleSpaceProviderContainer: React.ComponentClass<DataRoleSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(DataRoleSpaceProviderAng);

const renderDataRoleSpace = (props: DataRoleSpaceProps): JSX.Element => <ConnectedDataRoleSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type DataRoleSpaceProviderProps = LocationPathProp & Omit<DataRoleSpaceProviderOwnProps, 'renderDataRoleSpace' | 'fetchedAllData'>;

export interface DataRoleSpaceProviderState {
  readonly fetchedAllData: boolean;
}

export class DataRoleSpaceProvider extends React.PureComponent<DataRoleSpaceProviderProps, DataRoleSpaceProviderState> {
  public constructor(props: DataRoleSpaceProviderProps) {
    super(props);
    this.state = {fetchedAllData: false};
  }

  public render() {
    const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
      routeLocationPath: this.props.routeLocationPath,
      routeLocationSearch: this.props.routeLocationSearch,
      shouldUpdateContentTypes: false,
      shouldUpdateFilters: false,
      shouldUpdateSort: false,
      shouldUpdateBreadcrumbs: this.state.fetchedAllData
    };

    const DataRoleSpaceInfo: SpaceInfo = {
      item: null,
      spaceType: 'dataRole',
      subspaceName: this.props.subspaceName,
    };

    return (
      <ConnectedSpaceInfoProvider spaceInfo={DataRoleSpaceInfo}>
        <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
          <ConnectedDataRoleSpaceProviderContainer
            {...this.props}
            renderDataRoleSpace={renderDataRoleSpace}
            fetchedAllData={this.fetchedAllData} />
        </ConnectedQueryParamUpdateProvider>
      </ConnectedSpaceInfoProvider>
    );
  }

  private fetchedAllData = () => {
    this.setState({fetchedAllData: true});
  }
}
